import {
  COOKIES_STORAGE_KEY,
  COOKIES_EXPIRE_STORAGE_KEY,
  LEGACY_COOKIES_STORAGE_KEY,
  LEGACY_COOKIES_EXPIRE_STORAGE_KEY,
} from './storageKeys'

export interface CookiesConsentState {
  necessary: boolean
  functional: boolean
  analytics: boolean
  performance: boolean
  advertisement: boolean
}

export const createDeclinedCookies = (): CookiesConsentState => ({
  necessary: true,
  functional: false,
  analytics: false,
  performance: false,
  advertisement: false,
})

export const createAcceptedCookies = (): CookiesConsentState => ({
  necessary: true,
  functional: true,
  analytics: true,
  performance: true,
  advertisement: true,
})

const COOKIES_VERSION_KEY = 'cookies_version_flag'
const COOKIES_VERSION_VALUE = '2'

const getStorage = (): Storage | null => {
  try {
    if (typeof window !== 'undefined' && 'localStorage' in window) {
      return window.localStorage
    }
  } catch (e) {}
  return null
}

const safeGet = (key: string): string | null => {
  const storage = getStorage()
  if (!storage) {
    return null
  }
  try {
    return storage.getItem(key)
  } catch (e) {
    return null
  }
}

const safeSet = (key: string, value: string) => {
  const storage = getStorage()
  if (!storage) {
    return
  }
  try {
    storage.setItem(key, value)
  } catch (e) {}
}

const safeRemove = (key: string) => {
  const storage = getStorage()
  if (!storage) {
    return
  }
  try {
    storage.removeItem(key)
  } catch (e) {}
}

let legacyPurged = false

export const purgeLegacyCookiesKeys = () => {
  const storage = getStorage()
  if (!storage) {
    return
  }
  if (legacyPurged) {
    return
  }
  legacyPurged = true
  try { storage.removeItem(LEGACY_COOKIES_STORAGE_KEY) } catch (e) {}
  try { storage.removeItem(LEGACY_COOKIES_EXPIRE_STORAGE_KEY) } catch (e) {}
}

const ensureLegacyPurged = () => {
  if (!legacyPurged) {
    purgeLegacyCookiesKeys()
  }
}

const setCookiesVersionFlag = () => {
  const storage = getStorage()
  if (!storage) {
    return
  }
  try {
    storage.setItem(COOKIES_VERSION_KEY, COOKIES_VERSION_VALUE)
  } catch (e) {}
}

export const isCookiesVersionValid = (): boolean => {
  const storage = getStorage()
  if (!storage) {
    return false
  }
  try {
    return storage.getItem(COOKIES_VERSION_KEY) === COOKIES_VERSION_VALUE
  } catch (e) {
    return false
  }
}

purgeLegacyCookiesKeys()

export const readCookiesRaw = (): string | null => {
  ensureLegacyPurged()
  return safeGet(COOKIES_STORAGE_KEY)
}

export const readCookies = <T = any>(): T | null => {
  const payload = readCookiesRaw()
  if (!payload) {
    return null
  }

  try {
    return JSON.parse(payload) as T
  } catch (e) {
    return null
  }
}

export const storeCookies = (value: any) => {
  ensureLegacyPurged()
  if (value == null) {
    safeRemove(COOKIES_STORAGE_KEY)
    safeRemove(COOKIES_VERSION_KEY)
    return
  }

  const payload = typeof value === 'string' ? value : JSON.stringify(value)
  safeSet(COOKIES_STORAGE_KEY, payload)
  setCookiesVersionFlag()
}

export const removeCookies = () => {
  ensureLegacyPurged()
  safeRemove(COOKIES_STORAGE_KEY)
  safeRemove(COOKIES_VERSION_KEY)
}

export const readCookiesExpireRaw = (): string | null => {
  ensureLegacyPurged()
  return safeGet(COOKIES_EXPIRE_STORAGE_KEY)
}

export const storeCookiesExpire = (value: string | null) => {
  ensureLegacyPurged()
  if (value == null) {
    safeRemove(COOKIES_EXPIRE_STORAGE_KEY)
    return
  }
  safeSet(COOKIES_EXPIRE_STORAGE_KEY, value)
  setCookiesVersionFlag()
}
